import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getCoursesByTeacher,
  addCourse,
  updateCourse,
  deleteCourse,
  addLessonToCourse,
  updateLessonInfo,
  deleteLessonFromCourse
} from '../../services/userService';
import {
  getCourseDetails
} from '../../services/courseService';
import { Course, Lesson, CourseLevel, CourseSubject } from '../../types';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Alert, 
  Spinner, 
  Form, 
  Modal, 
  Badge, 
  InputGroup, 
  Dropdown 
} from 'react-bootstrap';
import { 
  FaSearch, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaFilter, 
  FaSortAmountUp, 
  FaSortAmountDown,
  FaBook,
  FaVideo,
  FaGraduationCap
} from 'react-icons/fa';
import CoursesList from '../../components/teacher/CoursesList';
import CourseDetails from '../../components/teacher/CourseDetails';
import LessonsList from '../../components/teacher/LessonsList';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';

const CoursesManagementPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [courseSortField, setCourseSortField] = useState<string>('title');
  
  // Form states for courses
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showDeleteCourseModal, setShowDeleteCourseModal] = useState(false);
  const [showCourseDetailsModal, setShowCourseDetailsModal] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState('');
  const [newCourseDescription, setNewCourseDescription] = useState('');
  const [newCourseLevel, setNewCourseLevel] = useState<CourseLevel>(CourseLevel.INTERMEDIATE);
  const [newCourseSubject, setNewCourseSubject] = useState<CourseSubject>(CourseSubject.OTHER);
  const [newCoursePrice, setNewCoursePrice] = useState('');
  const [newCourseDuration, setNewCourseDuration] = useState('');
  const [isCourseEditing, setIsCourseEditing] = useState(false);
  const [courseImage, setCourseImage] = useState<File | null>(null);
  const [activeCourseTab, setActiveCourseTab] = useState('details');

  useEffect(() => {
    if (currentUser) {
      fetchCourses();
    }
  }, [currentUser]);

  const fetchCourses = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      const fetchedCourses = await getCoursesByTeacher(currentUser.uid);
      setCourses(fetchedCourses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('حدث خطأ أثناء جلب بيانات الكورسات');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDetails = async (courseId: string) => {
    try {
      setLoading(true);
      setError(null);
      const courseDetails = await getCourseDetails(courseId);
      setSelectedCourse(courseDetails);
      setLessons(courseDetails.lessons || []);
    } catch (error) {
      console.error('Error fetching course details:', error);
      setError('حدث خطأ أثناء جلب تفاصيل الكورس');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourse = async () => {
    try {
      if (!currentUser) {
        setError('يجب تسجيل الدخول لإنشاء كورس');
        return;
      }

      setLoading(true);
      setError(null);
      
      let imageUrl = '';
      
      if (courseImage) {
        const storageRef = ref(storage, `courses/${Date.now()}_${courseImage.name}`);
        const uploadResult = await uploadBytes(storageRef, courseImage);
        imageUrl = await getDownloadURL(uploadResult.ref);
      }
      
      const courseData: Omit<Course, 'id' | 'createdAt' | 'updatedAt'> = {
        title: newCourseTitle,
        description: newCourseDescription,
        level: newCourseLevel,
        subject: newCourseSubject,
        price: parseFloat(newCoursePrice) || 0,
        duration: parseInt(newCourseDuration) || 0,
        teacherId: currentUser.uid,
        teacherName: currentUser.displayName || 'Unknown Teacher',
        isPublished: false,
        studentsCount: 0,
        lessonsCount: 0,
        enrolledCount: 0,
        imageUrl
      };
      
      await addCourse(courseData);
      setShowCourseModal(false);
      resetCourseForm();
      await fetchCourses();
    } catch (error) {
      console.error('Error adding course:', error);
      setError('حدث خطأ أثناء إضافة الكورس');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCourse = async () => {
    if (!selectedCourse) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let imageUrl = selectedCourse.imageUrl || '';
      
      if (courseImage) {
        const storageRef = ref(storage, `courses/${Date.now()}_${courseImage.name}`);
        const uploadResult = await uploadBytes(storageRef, courseImage);
        imageUrl = await getDownloadURL(uploadResult.ref);
      }
      
      const courseData: Partial<Course> = {
        title: newCourseTitle,
        description: newCourseDescription,
        level: newCourseLevel,
        subject: newCourseSubject,
        price: parseFloat(newCoursePrice) || 0,
        duration: parseInt(newCourseDuration) || 0,
        imageUrl
      };
      
      await updateCourse(selectedCourse.id, courseData);
      setShowCourseModal(false);
      resetCourseForm();
      await fetchCourses();
    } catch (error) {
      console.error('Error updating course:', error);
      setError('حدث خطأ أثناء تحديث الكورس');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourse) return;
    
    try {
      setLoading(true);
      setError(null);
      await deleteCourse(selectedCourse.id);
      setShowDeleteCourseModal(false);
      setSelectedCourse(null);
      await fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      setError('حدث خطأ أثناء حذف الكورس');
    } finally {
      setLoading(false);
    }
  };

  const resetCourseForm = () => {
    setNewCourseTitle('');
    setNewCourseDescription('');
    setNewCourseLevel(CourseLevel.INTERMEDIATE);
    setNewCourseSubject(CourseSubject.OTHER);
    setNewCoursePrice('');
    setNewCourseDuration('');
    setCourseImage(null);
    setIsCourseEditing(false);
    setSelectedCourse(null);
  };

  const toggleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  const filteredCourses = courses.filter(course => 
    course.title.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
    (course.description && course.description.toLowerCase().includes(courseSearchTerm.toLowerCase())) ||
    (course.subject && course.subject.toLowerCase().includes(courseSearchTerm.toLowerCase()))
  ).sort((a, b) => {
    let valueA: any = a[courseSortField as keyof Course];
    let valueB: any = b[courseSortField as keyof Course];
    
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
    }
    
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleCourseImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCourseImage(e.target.files[0]);
    }
  };

  return (
    <Container fluid className="py-4">
      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" role="status" />
          <p className="mt-2">جاري تحميل البيانات...</p>
        </div>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <h1 className="mb-4">إدارة الكورسات</h1>
        </Col>
      </Row>
      
      {/* Courses List */}
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h2>الكورسات</h2>
            </Col>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control 
                  type="text" 
                  placeholder="ابحث عن الكورسات..." 
                  value={courseSearchTerm} 
                  onChange={(e) => setCourseSearchTerm(e.target.value)} 
                />
              </InputGroup>
            </Col>
            <Col md={3} className="d-flex justify-content-end">
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="outline-secondary" id="dropdown-sort">
                  <FaFilter /> ترتيب
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setCourseSortField('title')}>
                    حسب العنوان {courseSortField === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setCourseSortField('price')}>
                    حسب السعر {courseSortField === 'price' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setCourseSortField('createdAt')}>
                    حسب تاريخ الإنشاء {courseSortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={toggleSortDirection}>
                    {sortDirection === 'asc' ? <><FaSortAmountUp /> تصاعدي</> : <><FaSortAmountDown /> تنازلي</>}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Button 
                variant="primary" 
                onClick={() => {
                  setIsCourseEditing(false);
                  resetCourseForm();
                  setShowCourseModal(true);
                }}
              >
                <FaPlus className="me-1" /> إضافة كورس
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {!loading && (
            <>
              {filteredCourses.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>عنوان الكورس</th>
                        <th>المستوى</th>
                        <th>السعر</th>
                        <th>عدد الدروس</th>
                        <th>عدد الطلاب</th>
                        <th>الحالة</th>
                        <th>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCourses.map((course, index) => (
                        <tr key={course.id}>
                          <td>{index + 1}</td>
                          <td>{course.title}</td>
                          <td>{course.level}</td>
                          <td>{course.price} ج.م</td>
                          <td>{course.lessonsCount || 0}</td>
                          <td>{course.enrolledCount || 0}</td>
                          <td>
                            {course.isPublished ? (
                              <Badge bg="success">منشور</Badge>
                            ) : (
                              <Badge bg="warning">مسودة</Badge>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button 
                                variant="info" 
                                size="sm" 
                                className="me-1" 
                                title="عرض التفاصيل"
                                onClick={() => {
                                  setSelectedCourse(course);
                                  fetchCourseDetails(course.id);
                                  setActiveCourseTab('details');
                                  setShowCourseDetailsModal(true);
                                }}
                              >
                                <FaEye />
                              </Button>
                              <Button 
                                variant="primary" 
                                size="sm" 
                                className="me-1" 
                                title="إدارة الحصص والفيديوهات"
                                onClick={() => {
                                  setSelectedCourse(course);
                                  fetchCourseDetails(course.id);
                                  setActiveCourseTab('lessons');
                                  setShowCourseDetailsModal(true);
                                }}
                              >
                                <FaVideo />
                              </Button>
                              <Button 
                                variant="warning" 
                                size="sm" 
                                className="me-1" 
                                title="تعديل"
                                onClick={() => {
                                  setSelectedCourse(course);
                                  setIsCourseEditing(true);
                                  setNewCourseTitle(course.title);
                                  setNewCourseDescription(course.description || '');
                                  setNewCourseLevel(course.level || CourseLevel.INTERMEDIATE);
                                  setNewCourseSubject(course.subject || CourseSubject.OTHER);
                                  setNewCoursePrice(course.price.toString());
                                  setNewCourseDuration(course.duration?.toString() || '');
                                  setShowCourseModal(true);
                                }}
                              >
                                <FaEdit />
                              </Button>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                title="حذف"
                                onClick={() => {
                                  setSelectedCourse(course);
                                  setShowDeleteCourseModal(true);
                                }}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted">لا توجد كورسات حالياً</p>
                  <Button 
                    variant="primary" 
                    onClick={() => {
                      setIsCourseEditing(false);
                      resetCourseForm();
                      setShowCourseModal(true);
                    }}
                  >
                    <FaPlus className="me-1" /> إضافة كورس جديد
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal for Adding/Editing Course */}
      <Modal show={showCourseModal} onHide={() => setShowCourseModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isCourseEditing ? 'تعديل الكورس' : 'إضافة كورس جديد'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="courseTitle">
              <Form.Label>عنوان الكورس</Form.Label>
              <Form.Control 
                type="text" 
                value={newCourseTitle} 
                onChange={(e) => setNewCourseTitle(e.target.value)} 
                placeholder="أدخل عنوان الكورس" 
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="courseDescription">
              <Form.Label>وصف الكورس</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                value={newCourseDescription} 
                onChange={(e) => setNewCourseDescription(e.target.value)} 
                placeholder="أدخل وصف الكورس" 
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="courseLevel">
                  <Form.Label>مستوى الكورس</Form.Label>
                  <Form.Select 
                    value={newCourseLevel} 
                    onChange={(e) => setNewCourseLevel(e.target.value as CourseLevel)}
                  >
                    <option value={CourseLevel.BEGINNER}>مبتدئ</option>
                    <option value={CourseLevel.INTERMEDIATE}>متوسط</option>
                    <option value={CourseLevel.ADVANCED}>متقدم</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="courseSubject">
                  <Form.Label>موضوع الكورس</Form.Label>
                  <Form.Select 
                    value={newCourseSubject} 
                    onChange={(e) => setNewCourseSubject(e.target.value as CourseSubject)}
                  >
                    <option value={CourseSubject.MATH}>رياضيات</option>
                    <option value={CourseSubject.SCIENCE}>علوم</option>
                    <option value={CourseSubject.PHYSICS}>فيزياء</option>
                    <option value={CourseSubject.CHEMISTRY}>كيمياء</option>
                    <option value={CourseSubject.BIOLOGY}>أحياء</option>
                    <option value={CourseSubject.ARABIC}>لغة عربية</option>
                    <option value={CourseSubject.ENGLISH}>لغة إنجليزية</option>
                    <option value={CourseSubject.HISTORY}>تاريخ</option>
                    <option value={CourseSubject.GEOGRAPHY}>جغرافيا</option>
                    <option value={CourseSubject.COMPUTER}>حاسب آلي</option>
                    <option value={CourseSubject.RELIGION}>تربية دينية</option>
                    <option value={CourseSubject.OTHER}>أخرى</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="coursePrice">
                  <Form.Label>سعر الكورس (ج.م)</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={newCoursePrice} 
                    onChange={(e) => setNewCoursePrice(e.target.value)} 
                    placeholder="أدخل سعر الكورس" 
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="courseDuration">
                  <Form.Label>مدة الكورس (بالساعات)</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={newCourseDuration} 
                    onChange={(e) => setNewCourseDuration(e.target.value)} 
                    placeholder="أدخل مدة الكورس" 
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3" controlId="courseImage">
              <Form.Label>صورة الكورس</Form.Label>
              <Form.Control 
                type="file" 
                onChange={handleCourseImageChange} 
                accept="image/*"
              />
              <Form.Text className="text-muted">
                اختر صورة للكورس (اختياري)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCourseModal(false)}>
            إلغاء
          </Button>
          <Button 
            variant="primary" 
            onClick={isCourseEditing ? handleUpdateCourse : handleAddCourse}
            disabled={!newCourseTitle}
          >
            {isCourseEditing ? 'تحديث' : 'إضافة'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal for Deleting Course */}
      <Modal show={showDeleteCourseModal} onHide={() => setShowDeleteCourseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>حذف الكورس</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          هل أنت متأكد من حذف الكورس "{selectedCourse?.title}"؟
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteCourseModal(false)}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={handleDeleteCourse}>
            حذف
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for Course Details */}
      <Modal
        show={showCourseDetailsModal}
        onHide={() => setShowCourseDetailsModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>تفاصيل الكورس</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCourse && (
            <CourseDetails
              course={selectedCourse}
              lessons={lessons}
              loading={loading}
              error={error}
              onBack={() => {
                setShowCourseDetailsModal(false);
                setSelectedCourse(null);
              }}
              onEditCourse={(course) => {
                setSelectedCourse(course);
                setShowCourseDetailsModal(false);
                setShowCourseModal(true);
              }}
              onDeleteCourse={async (courseId) => {
                setSelectedCourse(courses.find(c => c.id === courseId) || null);
                setShowCourseDetailsModal(false);
                setShowDeleteCourseModal(true);
                return Promise.resolve();
              }}
              onEditLesson={(lesson) => {
                // Handle lesson edit in the LessonsList component
                console.log('Edit lesson:', lesson);
              }}
              onDeleteLesson={async (lessonId) => {
                try {
                  if (selectedCourse) {
                    await deleteLessonFromCourse(selectedCourse.id, lessonId);
                    // Update the lessons list after deletion
                    setLessons(prevLessons => prevLessons.filter(lesson => lesson.id !== lessonId));
                  }
                } catch (error) {
                  console.error('Error deleting lesson:', error);
                  setError('حدث خطأ أثناء حذف الدرس');
                }
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCourseDetailsModal(false)}>
            إغلاق
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CoursesManagementPage;
